"use strict";
/**
* Methods that manipulate on datetime.
*
* @module
* */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.lastDayOf = exports.yearOf = exports.diff = exports.subtract = exports.startOfDate = exports.startOf = exports.secondOf = exports.monthOf = exports.minuteOf = exports.millisecondOf = exports.isSameOrBefore = exports.isSameOrAfter = exports.isSame = exports.isBefore = exports.isAfter = exports.hourOf = exports.endOf = exports.dayOfWeekOf = exports.dayOf = exports.compare = exports.addYear = exports.addMonth = exports.addDay = exports.add = exports.now = exports.valueOf = exports.isValid = exports.toISO = void 0;
var moment_timezone_1 = __importDefault(require("moment-timezone"));
var compose_1 = __importDefault(require("lodash/fp/compose"));
var invoke_1 = __importDefault(require("lodash/fp/invoke"));
var isNil_1 = __importDefault(require("lodash/fp/isNil"));
var isNumber_1 = __importDefault(require("lodash/fp/isNumber"));
var negate_1 = __importDefault(require("lodash/fp/negate"));
var utils_1 = require("./utils");
var constants_1 = require("./constants");
var format_1 = require("./format");
var timeZone_1 = require("./timeZone");
var ISOStandard_1 = require("./ISOStandard");
var getMomentCreator = function (datetime) {
    return /[+-]\d{2}:\d{2}|Z$/.test(datetime)
        ? moment_timezone_1.default.parseZone
        : moment_timezone_1.default;
};
var parseUTCOffset = function (datetime) { return datetime.replace(/\+00:00$/, 'Z'); };
var syncZone = function (datetime) { return (0, compose_1.default)(timeZone_1.convertZone, timeZone_1.getUTCOffset)(datetime); };
/**
* Generate a standard ISO 8601 datetime string.
*
* @param source - Any date or time string/timestamp presentation
* @param format - The format of the datetime presentation
* @param timeZone - The time zone of the datetime presentation
*
* @example
* ```typescript
* toISO(1648692243); // '1970-01-20T01:58:12.243Z'
* toISO('2018-12-12T09:00:00.000+08:00'); // 2018-12-12T09:00:00.000+08:00
* toISO('2018-12-12 09:00:00', 'YYYY-MM-DD hh:mm:ss');
* toISO('8:30 PM', 'h:mm a', 'UTC'); // {today}T20:30:00.000Z
* toISO('foo'); // null
* toISO('25', 'h:mm a'); // null
* ```
* */
var toISO = function (source, format, timeZone) {
    var datetime = source;
    if ((0, isNumber_1.default)(datetime)) {
        datetime = new Date(datetime).toISOString();
    }
    if (!datetime) {
        return null;
    }
    var hasValidZone = moment_timezone_1.default.tz.names().indexOf(timeZone) > -1;
    var create = hasValidZone ? moment_timezone_1.default.tz : getMomentCreator(datetime);
    var momentInstance = create.apply(void 0, [datetime, format, timeZone].filter((0, negate_1.default)(isNil_1.default)));
    return momentInstance.isValid()
        ? parseUTCOffset(momentInstance.format(constants_1.ISO8601_DATE_FOMART))
        : null;
};
exports.toISO = toISO;
/**
 * Test whether the source input is a valid datetime.
 *
 * When pass in the `format`, it will instead check whether the `date/time` is a valid
 * presentation in the passed in `format`.
 *
 * @param source - Any date/time presentation
 * @param format - The date presentation's format
 *
 * @example
 * ```typescript
 * isValid('2023-11-22T15:58:43.222Z') // true
 * isValid('2023-11-22T15:58:43Z') // true
 * isValid('2023-11-22T15:58Z') // false
 * ```
 * */
var isValid = function (source, format) {
    if (format === void 0) { format = (0, ISOStandard_1.guessISOFormat)(source); }
    return !!source
        && (0, moment_timezone_1.default)(source, (0, utils_1.parseFormat)(format), true).isValid()
        && (0, compose_1.default)(parseUTCOffset, (0, format_1.format)(format))((0, exports.toISO)(source, format)) === parseUTCOffset(source);
};
exports.isValid = isValid;
/**
 * Get the full timestamp of given datetime.
 *
 * @param datetime - ISO datetime string
 *
 * @example
 * ```typescript
 * valueOf('2000-01-01T00:00:00.123Z') // 946684800123
 * ```
 * */
exports.valueOf = (0, compose_1.default)((0, invoke_1.default)('valueOf'), moment_timezone_1.default);
/**
* Get current time in ISO 8601 format.
*
* @example
* ```typescript
* now() // 2023-07-31T11:14:52.294+08:00
* ```
* */
var now = function () { return (0, timeZone_1.convertZone)((0, timeZone_1.getLocalZone)())(new Date().toISOString()); };
exports.now = now;
/**
* Add given duration to the datetime.
*
* @param duration - The duration to add
*
* @example
* ```typescript
* add({ years: 1 })('2021-12-08T15:17:07.123Z') // '2022-12-08T15:17:07.123Z'
* add({ hours: 10, minutes: 50, seconds: 58 })('2021-12-08T15:17:07.123Z') // '2021-12-09T02:08:05.123Z'
* add({ days: 2 })('2021-12-08T15:27:00.000-06:00') // '2021-12-10T15:27:00.000-06:00'
* ```
* */
var add = function (duration) { return function (datetime) { return (syncZone(datetime)(moment_timezone_1.default.parseZone(datetime).add(duration).toISOString())); }; };
exports.add = add;
/**
 * Manipulate given datetime by adding or subtracting days
 *
 * @param unit - The days to be added.
 *
 * @example
 * ```typescript
 * addDay(2)('2020-01-01T00:00:00.123Z') // '2020-01-03T00:00:00.123Z'
 * ```
 * */
var addDay = function (unit) { return (0, exports.add)({ days: unit }); };
exports.addDay = addDay;
/**
 * Manipulate given datetime by adding or subtracting months
 *
 * @param unit - The months to be added.
 *
 * @example
 * ```typescript
 * addMonth(2)('2020-01-01T00:00:00.123Z') // '2020-03-01T00:00:00.123Z'
 * ```
 * */
var addMonth = function (unit) { return (0, exports.add)({ months: unit }); };
exports.addMonth = addMonth;
/**
 * Manipulate given datetime by adding or subtracting years
 *
 * @param unit - The years to be added.
 *
 * @example
 * ```typescript
 * addYear(2)('2020-01-01T00:00:00.123Z') // '2022-01-01T00:00:00.123Z'
 * ```
 * */
var addYear = function (unit) { return (0, exports.add)({ years: unit }); };
exports.addYear = addYear;
/**
* Compare two datetime
*
* @param source - The datetime to be compared
* @param target - The datetime to be compared to
*
* @example
* ```typescript
* compare('2021-12-07T13:57:00.000+07:00', '2021-12-07T13:57:00.000+08:00') // 1
* compare('2021-12-07T13:57:00.000+07:00', '2021-12-07T14:57:00.000+08:00') // 0
* compare('2021-12-07T13:57:00.000+07:00', '2021-12-07T15:57:00.000+08:00') // -1
* ```
* */
var compare = function (source, target) {
    var compareResult = (0, exports.valueOf)(source) - (0, exports.valueOf)(target);
    return compareResult === 0
        ? compareResult
        : compareResult / Math.abs(compareResult);
};
exports.compare = compare;
/**
* Get the day of the datetime
*
* @param datetime
*
* @example
* ```typescript
* dayOf('2000-06-20T02:30:10.333+08:00') // 20
* ```
* */
var dayOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).date();
};
exports.dayOf = dayOf;
/**
* Get the day-of-week of the datetime.
*
* It's index is start from "1" to "7" which stands for
* "Monday" to "Sunday"
*
* @param datetime
*
* @example
* ```typescript
* dayOfWeekOf('2022-03-08T02:00:00.000+08:00') // 2
* ```
* */
var dayOfWeekOf = function (datetime) {
    var dayOfWeek = moment_timezone_1.default.parseZone(datetime).day();
    return dayOfWeek === 0 ? 7 : dayOfWeek;
};
exports.dayOfWeekOf = dayOfWeekOf;
/**
 * Get the end-of given unit of the datetime.
 *
 * @param options
 * @param options.smallestUnit - The smallest unit that the `endOf` calculate from.
 *
 * @example
 * ```typescript
 * endOf({ smallestUnit: 'year' })('2020-12-07T16:29:42.000+08:00') // '2020-12-31T23:59:59.999+08:00'
 * endOf({ smallestUnit: 'month' })('2021-02-07T16:29:42.000+08:00') // '2021-02-28T23:59:59.999+08:00'
 * endOf({ smallestUnit: 'day' })('2021-12-07T16:53:00.000+08:00') // '2021-12-07T23:59:59.999+08:00'
 * ```
 * */
var endOf = function (_a) {
    var smallestUnit = _a.smallestUnit;
    return function (datetime) {
        return syncZone(datetime)(moment_timezone_1.default.parseZone(datetime).endOf(smallestUnit).toISOString());
    };
};
exports.endOf = endOf;
/**
 * Get the hour of the datetime
 *
 * @param datetime
 *
 * @example
 * ```typescript
 * hourOf('2000-01-01T02:00:00.000') // 2
 * ```
* */
var hourOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).hour();
};
exports.hourOf = hourOf;
/**
 * Determine whether the source datetime is after the target datetime.
 *
 * @example
 * ```typescript
 * isAfter('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.000Z') // false
 * isAfter('2021-12-07T14:00:00.000-01:00')('2021-12-07T14:00:00.000Z') // false
 * isAfter('2021-12-07T14:00:00.000Z')('2021-12-07T15:00:00.000Z') // true
 * isAfter('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.001Z') // true
 * ```
 * */
var isAfter = function (target) { return function (source) { return (0, exports.compare)(source, target) > 0; }; };
exports.isAfter = isAfter;
/**
 * Determine whether the source datetime is before the target datetime.
 *
 * @example
 * ```typescript
 * isBefore('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.000Z') // false
 * isBefore('2021-12-07T14:00:00.000Z')('2021-12-07T15:00:00.000Z') // false
 * isBefore('2021-12-07T14:00:00.001Z')('2021-12-07T14:00:00.000Z') // true
 * isBefore('2021-12-07T14:00:00.000-01:00')('2021-12-07T14:00:00.000Z') // true
 * ```
 * */
var isBefore = function (target) { return function (source) { return (0, exports.compare)(source, target) < 0; }; };
exports.isBefore = isBefore;
/**
 * Detemine whether the source datetime is the same as the target.
 *
 * @example
 * ```typescript
 * isSame('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.000Z') // true
 * isSame('2021-12-07T14:00:00.000Z')('2021-12-07T13:00:00.000-01:00') // true
 * isSame('2021-12-07T14:00:00.000Z')('2020-12-07T14:00:00.000Z') // false
 * ```
 * */
var isSame = function (target) { return function (source) { return (0, exports.compare)(source, target) === 0; }; };
exports.isSame = isSame;
/**
 * Determine whether the source datetime is the same or after the target datetime.
 *
 * @example
 * ```typescript
 * isSameOrAfter('2021-12-07T14:00:00.000-01:00')('2021-12-07T14:00:00.000Z') // false
 * isSameOrAfter('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.000Z') // true
 * isSameOrAfter('2021-12-07T14:00:00.000Z')('2021-12-07T15:00:00.000Z') // true
 * isSameOrAfter('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.001Z') // true
 * ```
 * */
var isSameOrAfter = function (target) { return function (source) { return (0, exports.compare)(source, target) >= 0; }; };
exports.isSameOrAfter = isSameOrAfter;
/**
 * Determine whether the source datetime is the same or before the target datetime.
 *
 * @example
 * ```typescript
 * isSameOrBefore('2021-12-07T14:00:00.000Z')('2021-12-07T15:00:00.000Z') // false
 * isSameOrBefore('2021-12-07T14:00:00.000Z')('2021-12-07T14:00:00.000Z') // true
 * isSameOrBefore('2021-12-07T14:00:00.001Z')('2021-12-07T14:00:00.000Z') // true
 * isSameOrBefore('2021-12-07T14:00:00.000-01:00')('2021-12-07T14:00:00.000Z') // true
 * ```
 * */
var isSameOrBefore = function (target) { return function (source) { return (0, exports.compare)(source, target) <= 0; }; };
exports.isSameOrBefore = isSameOrBefore;
/**
* Get the millisecond of the datetime
*
* @param datetime
*
* @example
* ```typescript
* millisecondOf('2000-01-01T02:30:30.668') // 668
* ```
* */
var millisecondOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).millisecond();
};
exports.millisecondOf = millisecondOf;
/**
* Get minute of the datetime
*
* @param datetime
*
* @example
* ```typescript
* minuteOf('2000-01-01T02:30:00.000-06:00') // 30
* ```
* */
var minuteOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).minute();
};
exports.minuteOf = minuteOf;
/**
* Get the month of the datetime.
*
* The index if start from "1" to "12".
*
* @param datetime
*
* @example
* ```typescript
* monthOf('2000-12-01T02:30:10.333Z') // 12
* ```
* */
var monthOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).month() + 1;
};
exports.monthOf = monthOf;
/**
* Get the second of the datetime
*
* @param datetime
*
* @example
* ```typescript
* secondOf('2000-01-01T02:00:20.000-06:00') // 20
* ```
* */
var secondOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).second();
};
exports.secondOf = secondOf;
/**
 * Get the start-of given unit of the datetime.
 *
 * @param options
 * @param options.smallestUnit - The smallest datetime unit to calculate from.
 *
 * @example
 * ```typescript
 * startOf({ smallestUnit: 'year' })('2021-12-08T10:08:10.000-06:00') // '2021-01-01T00:00:00.000-06:00'
 * startOf({ smallestUnit: 'month' })('2021-12-08T10:41:12.123-06:00') // '2021-12-01T00:00:00.000-06:00'
 * startOf({ smallestUnit: 'hour' })('2021-12-08T10:46:01.123-06:00') // '2021-12-08T10:00:00.000-06:00'
 * ```
 * */
var startOf = function (_a) {
    var smallestUnit = _a.smallestUnit;
    return function (datetime) {
        return syncZone(datetime)(moment_timezone_1.default.parseZone(datetime).startOf(smallestUnit).toISOString());
    };
};
exports.startOf = startOf;
/**
 * Get the start of the datetime.
 *
 * @deprecated
 * Use `startOf` instead, will be removed in v5
 *
 * @param datetime - ISO datetime string
 *
 * @example
 * ```typescript
 * startOfDate('2021-06-28T18:12:23.233+08:00') // '2021-06-28T00:00:00.000+08:00'
 * ```
* */
var startOfDate = function (datetime) { return syncZone(datetime)(moment_timezone_1.default.parseZone(datetime)
    .startOf('day')
    .toISOString()); };
exports.startOfDate = startOfDate;
/**
 * Subtract given duration from the datetime
 *
 * @param duration - The duration to subtract
 * @param datetime
 *
 * @example
 * ```typescript
 * subtract({ days: 1 })('2021-12-07T13:14:40.000Z') // '2021-12-06T13:14:40.000Z'
 * subtract({ hours: 14 })('2021-12-07T13:14:40.000+08:00') // '2021-12-06T23:14:40.000+08:00'
 * ```
 * */
var subtract = function (duration) { return function (datetime) {
    return syncZone(datetime)(moment_timezone_1.default.parseZone(datetime).subtract(duration).toISOString());
}; };
exports.subtract = subtract;
/**
* Get the diff of two datetime with the given `DatetimeUnit`.
*
* @param source
* @param unit
* @param target
*
* @example
* ```typescript
* diff('2024-03-10T08:10:20.333Z', 'year')('2022-03-10T08:10:20.333Z') // 2
* diff('2022-03-10T08:00:00.000-08:00', 'hour')('2022-03-10T08:00:00.000Z') // 8
* ```
* */
var diff = function (source, unit) { return function (target) {
    return moment_timezone_1.default.parseZone(source).diff(moment_timezone_1.default.parseZone(target), "".concat(unit), true);
}; };
exports.diff = diff;
/**
* Get the year of the datetime
*
* @param datetime
*
* @example
* ```typescript
* yearOf('2000-01-01T02:00:00.000+08:00') // 2000
* ```
* */
var yearOf = function (datetime) {
    return moment_timezone_1.default.parseZone(datetime).year();
};
exports.yearOf = yearOf;
/**
* Get the last day of a given month
*
* @param year
* @param month
*
* @example
* ```typescript
* lastDayOf(2000, 2) // 29
* ```
* */
var lastDayOf = function (year, month) {
    return new Date(year, month, 0).getDate();
};
exports.lastDayOf = lastDayOf;
